import {
  type Prisma,
  prisma,
  TemplateFor,
  TemplateType,
  TriggerEntityType,
  TriggerEvent,
  BookableItemType,
} from "client";
import {
  getDefaultTemplateByEventIdAndTemplateFor,
  getDefaultTemplateByWhiteLabelIdAndTemplateFor,
} from "services/defaultTemplate";

const triggerEventToTemplateFor: Partial<Record<TriggerEvent, TemplateFor>> = {
  // Direct mappings
  REQUEST_SUBMITTED: TemplateFor.REQUEST_SUBMITTED,
  REQUEST_ACCEPTED: TemplateFor.REQUEST_ACCEPTED,
  REQUEST_REJECTED: TemplateFor.REQUEST_REJECTED,
  REQUEST_REVISION: TemplateFor.REQUEST_REVISION,
  PAYMENT_OFFLINE_WIRE_TRANSFER: TemplateFor.PAYMENT_OFFLINE_WIRE_TRANSFER,
  PAYMENT_OFFLINE_CLIQ: TemplateFor.PAYMENT_OFFLINE_CLIQ,
  PAYMENT_OFFLINE_EFAWATEERCOM: TemplateFor.PAYMENT_OFFLINE_EFAWATEERCOM,
  PAYMENT_OFFLINE_POS: TemplateFor.PAYMENT_OFFLINE_POS,
  PAYMENT_OFFLINE_CASH: TemplateFor.PAYMENT_OFFLINE_CASH,
  PAYMENT_COMPLETED: TemplateFor.PAYMENT_COMPLETED,
  PAYMENT_REFUNDED: TemplateFor.PAYMENT_REFUNDED,
  PAYMENT_DECLINED: TemplateFor.PAYMENT_DECLINED,
  ORDER_COMPLETED: TemplateFor.ORDER_COMPLETED,
  ORGANIZER_TEMPORARY_VENUE_ACCESS:
    TemplateFor.ORGANIZER_TEMPORARY_VENUE_ACCESS,
  ADMIN_CREATION: TemplateFor.ADMIN_CREATION,
  ORGANIZER_TEAM_INVITATION: TemplateFor.ORGANIZER_TEAM_INVITATION,
  PROFILE_CREATION: TemplateFor.PROFILE_CREATION,
  NETWORKING_HUB_ACCESS: TemplateFor.NETWORKING_HUB_ACCESS,
  OTP_SENT: TemplateFor.OTP_SENT,
  MEETING_REQUESTED: TemplateFor.MEETING_REQUESTED,
  CONNECTION_REQUESTED: TemplateFor.CONNECTION_REQUESTED,
  TASK_ASSIGNED: TemplateFor.TASK_ASSIGNED,
  TASK_ACCEPTED: TemplateFor.TASK_ACCEPTED,
  TASK_REJECTED: TemplateFor.TASK_REJECTED,
  ORGANIZER_SIGNUP: TemplateFor.ORGANIZER_SIGNUP,
};

export enum TriggerEventEnum {
  // Notification templates
  REQUEST_SUBMITTED = "REQUEST_SUBMITTED",
  REQUEST_ACCEPTED = "REQUEST_ACCEPTED",
  REQUEST_REJECTED = "REQUEST_REJECTED",
  REQUEST_REVISION = "REQUEST_REVISION",
  INVITATION_SENT_NO_MAYBE = "INVITATION_RSVP_NO_MAYBE",
  INVITATION_SENT = "INVITATION_RSVP_MAYBE",
  ANY_INVITATION_RESPONSE = "ANY_INVITATION_RESPONSE",

  // Payment templates
  PAYMENT_OFFLINE_WIRE_TRANSFER = "PAYMENT_OFFLINE_WIRE_TRANSFER",
  PAYMENT_OFFLINE_CLIQ = "PAYMENT_OFFLINE_CLIQ",
  PAYMENT_OFFLINE_EFAWATEERCOM = "PAYMENT_OFFLINE_EFAWATEERCOM",
  PAYMENT_OFFLINE_POS = "PAYMENT_OFFLINE_POS",
  PAYMENT_OFFLINE_CASH = "PAYMENT_OFFLINE_CASH",
  PAYMENT_COMPLETED = "PAYMENT_COMPLETED",
  PAYMENT_REFUNDED = "PAYMENT_REFUNDED",
  PAYMENT_DECLINED = "PAYMENT_DECLINED",
  ORDER_COMPLETED = "ORDER_COMPLETED",

  // Organizer and admin templates
  ORGANIZER_TEMPORARY_VENUE_ACCESS = "ORGANIZER_TEMPORARY_VENUE_ACCESS",
  ADMIN_CREATION = "ADMIN_CREATION",
  ORGANIZER_TEAM_INVITATION = "ORGANIZER_TEAM_INVITATION",
  PROFILE_CREATION = "PROFILE_CREATION",
  NETWORKING_HUB_ACCESS = "NETWORKING_HUB_ACCESS",
  OTP_SENT = "OTP_SENT",
  MEETING_REQUESTED = "MEETING_REQUESTED",
  CONNECTION_REQUESTED = "CONNECTION_REQUESTED",
  TASK_ASSIGNED = "TASK_ASSIGNED",
  TASK_ACCEPTED = "TASK_ACCEPTED",
  TASK_REJECTED = "TASK_REJECTED",
}

//   CREATE

export const createTrigger = async (
  data: Prisma.TriggerCreateInput,
  type: TriggerEntityType,
  templateId: string,
  eventId: string
) => {
  return await prisma.trigger.create({
    data: {
      ...data,
      event: {
        connect: {
          id: eventId,
        },
      },
      triggerEntity: {
        create: {
          type,
          entityId: templateId,
        },
      },
    },
  });
};

//   GET

export const getTrigger = async (id: string) => {
  return await prisma.trigger.findUnique({
    where: { id },
  });
};

export const getTriggers = async () => {
  return await prisma.trigger.findMany();
};

export const getTriggersByEvent = async (
  eventId: string,
  triggerEvent: TriggerEvent,
  invitationId?: string,
  floorPlanId?: string,
  RSVPCount?: number,
  notificationTemplateId?: string
) => {
  let triggers: any[] = [];

  triggers = await prisma.trigger.findMany({
    where: {
      eventId,
      triggerEvent,
      triggerEntity: {
        ...(notificationTemplateId && { entityId: notificationTemplateId }),
        notification: {
          OR: [
            { deletedAt: null },
            { deletedAt: { not: { equals: undefined } } },
          ],
        },
      },
      ...(invitationId && {
        triggerEntity: {
          notification: { invitations: { some: { id: invitationId } } },
        },
      }),
      ...(floorPlanId && {
        triggerEntity: {
          notification: {
            floorPlans: { some: { id: floorPlanId, deletedAt: null } },
          },
        },
      }),
    },
    include: {
      triggerEntity: {
        include: {
          notification: {
            include: {
              // TODO: This is done for Backward compatibility with the old order items
              // Updated: tickets are now accessed via bookableItems
              bookableItems: {
                where: { type: BookableItemType.TICKET },
                include: { ticket: true },
              },
              invitations: true,
            },
          },
          design: true,
        },
      },
    },
  });

  // TODO: This is done for Backward compatibility with the old order items
  // Transform to maintain backward compatibility: extract tickets from bookableItems
  triggers = triggers.map((trigger) => {
    if (trigger.triggerEntity?.notification) {
      const notification = trigger.triggerEntity.notification as any;
      // Map bookableItems to tickets for backward compatibility
      const tickets =
        notification.bookableItems
          ?.map((bi: any) => bi.ticket)
          .filter(Boolean) || [];

      return {
        ...trigger,
        triggerEntity: {
          ...trigger.triggerEntity,
          notification: {
            ...notification,
            tickets, // Backward compatible tickets array
            bookableItems: notification.bookableItems, // Keep new structure too
          },
        },
      };
    }
    return trigger;
  });

  let isFromDefaultTemplate = false;
  if (triggers.length === 0) {
    console.log("RSVPCount", RSVPCount);
    const triggerEventEnum: TemplateFor[] =
      triggerEvent == TriggerEvent.INVITATION_SENT &&
      RSVPCount &&
      RSVPCount == 2
        ? [TriggerEventEnum.INVITATION_SENT_NO_MAYBE]
        : [TriggerEventEnum[triggerEvent as keyof typeof TriggerEventEnum]];

    const defaultTemplates = await getDefaultTemplateByEventIdAndTemplateFor(
      eventId,
      triggerEventEnum as TemplateFor[]
    );

    const notificationTemplates = [
      TemplateType.EMAIL,
      TemplateType.SMS,
      TemplateType.WHATSAPP,
    ];
    if (!defaultTemplates || "error" in defaultTemplates) {
      triggers = [];
    } else {
      triggers = defaultTemplates.map((template) => ({
        ...template,
        type: notificationTemplates.includes(template.type as any)
          ? TriggerEntityType.NOTIFICATION
          : TriggerEntityType.DESIGN,
      }));
      isFromDefaultTemplate = true;
    }
  }

  return { triggers, isFromDefaultTemplate };
};
export const getTriggersByWhiteLabel = async (
  whiteLabelId: string,
  triggerEvent: TriggerEvent,
  invitationId?: string,
  floorPlanId?: string,
  RSVPCount?: number,
  notificationTemplateId?: string
) => {
  let triggers: any[] = [];
  let isFromDefaultTemplate = false;
  const defaultTemplates = await getDefaultTemplateByWhiteLabelIdAndTemplateFor(
    whiteLabelId,
    [triggerEventToTemplateFor[triggerEvent]] as TemplateFor[]
  );

  const notificationTemplates = [
    TemplateType.EMAIL,
    TemplateType.SMS,
    TemplateType.WHATSAPP,
  ];
  if (!defaultTemplates || "error" in defaultTemplates) {
    triggers = [];
  } else {
    triggers = defaultTemplates.map((template) => ({
      ...template,
      type: notificationTemplates.includes(template.type as any)
        ? TriggerEntityType.NOTIFICATION
        : TriggerEntityType.DESIGN,
    }));
    isFromDefaultTemplate = true;
  }

  return { triggers, isFromDefaultTemplate };
};
export const getTriggerEventByEventId = async (eventId: string) => {
  try {
    const triggers = await prisma.trigger.findMany({
      where: {
        eventId,
        isActive: true,
      },
      distinct: ["triggerEvent"],
      select: {
        triggerEvent: true,
      },
    });
    const defaultTemplates = await prisma.defaultTemplate.findMany({
      where: { eventId, isActive: true },
      select: {
        id: true,
        // triggerEvent: true,        templateFor: true,
      },
    });
    return { triggers, defaultTemplates };
  } catch (e: any) {
    throw new Error(e);
  }
};
//   UPDATE

export const updateTrigger = async (
  id: string,
  data: Prisma.TriggerUpdateInput
) => {
  return await prisma.trigger.update({
    where: { id },
    data,
  });
};

//   DELETE

export const deleteTrigger = async (id: string) => {
  return await prisma.trigger.delete({
    where: { id },
  });
};
